import { point } from '@turf/helpers';
import distance from '@turf/distance';
import {
  decodeLatLong,
  parseMGRS,
  parseUTM_Band,
  parseUTM_NS,
} from '../../utils/useful_functions';
import { DESCRIPTION_MAX_LENGTH, NAME_MAX_LENGTH } from 'src/app.constants';
import { newSiteName } from 'src/utils/mapUtils';

export type SiteCsvColumnType =
  | 'name'
  | 'lat'
  | 'lon'
  | 'utm'
  | 'mgrs'
  | 'max_height_m'
  | 'max_height_ft'
  | 'description'
  | 'node_type'
  | 'ignore';

export type SiteCsvColumnErrorType = SiteCsvColumnType | 'max_height';

export type SiteCsvColumnError = {
  kind: 'duplicate' | 'missing';
  columnType: SiteCsvColumnErrorType;
} | null;

export type ParsedSiteCSVRow = {
  data: string[];
  errors: number[];
  nearbySite: boolean;
  truncates?: number[];
  siteData?: Record<string, any>;
};

type ParseSiteCSVOptions = {
  skipFirstRow: boolean;
  areNetworkSites: boolean;
  columnTypes: SiteCsvColumnType[];
  siteNameTemplate: string;
  count: number;
};

const FEET_PER_METER = 3.28083989;

const DUPLICATE_COLUMN_TYPE_MAP: Partial<
  Record<SiteCsvColumnType, SiteCsvColumnErrorType>
> = {
  max_height_m: 'max_height',
  max_height_ft: 'max_height',
};

const DEFAULT_NETWORK_COLUMN_TYPES: SiteCsvColumnType[] = [
  'name',
  'lat',
  'lon',
  'max_height_m',
  'description',
  'node_type',
];

const DEFAULT_SUBSCRIBER_COLUMN_TYPES: SiteCsvColumnType[] = [
  'name',
  'lat',
  'lon',
  'max_height_m',
  'description',
];

function getCellValue(row: string[], col?: number | null) {
  if (col == null) {
    return '';
  }
  return String(row[col] ?? '').trim();
}

function getMappedColumns(
  columnTypes: SiteCsvColumnType[],
  type: SiteCsvColumnType
) {
  return columnTypes.reduce<number[]>((result, columnType, index) => {
    if (columnType === type) {
      result.push(index);
    }
    return result;
  }, []);
}

function getMappedColumn(
  columnTypes: SiteCsvColumnType[],
  type: SiteCsvColumnType
) {
  return getMappedColumns(columnTypes, type)[0];
}

function tryParseUTM(value: string) {
  try {
    return parseUTM_NS(value);
  } catch (error) {
    return parseUTM_Band(value);
  }
}

export function getNearbySites(
  existingSites: any[],
  newSiteCoords: [number, number]
) {
  let to = null;
  try {
    to = point(newSiteCoords);
  } catch (error) {
    return false;
  }
  return existingSites.some((site: any) => {
    const from = point(site.geometry.coordinates);
    const gap = distance(from, to);
    return gap <= 0.001;
  });
}

export function getDefaultSiteCsvColumnTypes(
  columnCount: number,
  areNetworkSites: boolean
) {
  const defaults = areNetworkSites
    ? DEFAULT_NETWORK_COLUMN_TYPES
    : DEFAULT_SUBSCRIBER_COLUMN_TYPES;

  return Array.from({ length: columnCount }, (_, index) => {
    return defaults[index] ?? 'ignore';
  });
}

export function getSiteCsvColumnError(
  columnTypes: SiteCsvColumnType[]
): SiteCsvColumnError {
  const duplicateCounts = columnTypes.reduce<
    Partial<Record<SiteCsvColumnErrorType, number>>
  >((result, columnType) => {
    const duplicateKey =
      DUPLICATE_COLUMN_TYPE_MAP[columnType] ?? columnType;
    if (duplicateKey !== 'description' && duplicateKey !== 'ignore') {
      result[duplicateKey] = (result[duplicateKey] ?? 0) + 1;
    }
    return result;
  }, {});

  const singularTypes: SiteCsvColumnErrorType[] = [
    'name',
    'lat',
    'lon',
    'utm',
    'mgrs',
    'max_height',
    'node_type',
  ];

  for (const type of singularTypes) {
    if ((duplicateCounts[type] ?? 0) > 1) {
      return { kind: 'duplicate', columnType: type };
    }
  }

  const hasLat = columnTypes.includes('lat');
  const hasLon = columnTypes.includes('lon');
  const hasMgrs = columnTypes.includes('mgrs');
  const hasUtm = columnTypes.includes('utm');
  const requiredTypes: SiteCsvColumnErrorType[] = ['name'];

  if (hasLat && hasLon) {
    requiredTypes.push('lat', 'lon');
  } else if (hasMgrs) {
    requiredTypes.push('mgrs');
  } else if (hasUtm) {
    requiredTypes.push('utm');
  } else {
    requiredTypes.push('lat', 'lon');
  }

  for (const type of requiredTypes) {
    if (!duplicateCounts[type]) {
      return { kind: 'missing', columnType: type };
    }
  }

  return null;
}

export function buildSiteCsvPostData(rows: ParsedSiteCSVRow[]) {
  return rows
    .map((row) => row.siteData)
    .filter((siteData): siteData is Record<string, any> => Boolean(siteData));
}

export function parseSiteCSV(
  inputData: string[][],
  sites: any[],
  options: ParseSiteCSVOptions
) {
  const { skipFirstRow, areNetworkSites, columnTypes, siteNameTemplate, count } =
    options;
  const effectiveSiteNameTemplate =
    siteNameTemplate ||
    (areNetworkSites ? 'Network Site ###' : 'Subscriber Site ###');
  const validData: ParsedSiteCSVRow[] = [];
  const errorData: ParsedSiteCSVRow[] = [];
  const truncData: ParsedSiteCSVRow[] = [];
  const siteWithNearSites: ParsedSiteCSVRow[] = [];
  const columnError = getSiteCsvColumnError(columnTypes);
  let isEmpty = false;
  let missingNameCount = 0;

  let data = inputData.map((row) => [...row]);
  if (data.length > 0 && skipFirstRow) {
    data = data.slice(1);
  }

  if (data.length === 0) {
    isEmpty = true;
  }

  data = data.filter((row) => row.length > 1);
  if (data.length === 0) {
    isEmpty = true;
  }
  data = data.map((row) => {
    if (row.length >= columnTypes.length) {
      return row;
    }
    return row.concat(Array(columnTypes.length - row.length).fill(''));
  });

  const nameCol = getMappedColumn(columnTypes, 'name');
  const latCol = getMappedColumn(columnTypes, 'lat');
  const lonCol = getMappedColumn(columnTypes, 'lon');
  const utmCol = getMappedColumn(columnTypes, 'utm');
  const mgrsCol = getMappedColumn(columnTypes, 'mgrs');
  const maxHeightMCol = getMappedColumn(columnTypes, 'max_height_m');
  const maxHeightFtCol = getMappedColumn(columnTypes, 'max_height_ft');
  const nodeTypeCol = getMappedColumn(columnTypes, 'node_type');
  const descriptionCols = getMappedColumns(columnTypes, 'description');

  data.forEach((originalRow) => {
    const row = [...originalRow];
    const errors: number[] = [];
    const truncates: number[] = [];

    let name = getCellValue(row, nameCol);
    if (!name) {
      missingNameCount += 1;
      name = newSiteName({
        siteNameTemplate: effectiveSiteNameTemplate,
        count,
        increment: missingNameCount,
      });
      if (nameCol != null) {
        row[nameCol] = name;
      }
    }
    if (name.length > NAME_MAX_LENGTH) {
      name = name.substring(0, NAME_MAX_LENGTH);
      if (nameCol != null) {
        row[nameCol] = name;
        truncates.push(nameCol);
      }
    }

    let description = descriptionCols
      .map((col) => getCellValue(row, col))
      .filter(Boolean)
      .join(', ');
    if (description.length > DESCRIPTION_MAX_LENGTH) {
      description = description.substring(0, DESCRIPTION_MAX_LENGTH);
      if (descriptionCols.length > 0) {
        truncates.push(descriptionCols[0]);
        if (descriptionCols.length === 1) {
          row[descriptionCols[0]] = description;
        }
      }
    }

    let maximumHeight = null;
    const rawHeightM = getCellValue(row, maxHeightMCol);
    const rawHeightFt = getCellValue(row, maxHeightFtCol);
    if (rawHeightM) {
      maximumHeight = Number(rawHeightM);
      if (isNaN(maximumHeight)) {
        errors.push(maxHeightMCol as number);
      }
    } else if (rawHeightFt) {
      maximumHeight = Number(rawHeightFt);
      if (isNaN(maximumHeight)) {
        errors.push(maxHeightFtCol as number);
      } else {
        maximumHeight = maximumHeight / FEET_PER_METER;
      }
    }

    let latitude = null;
    let longitude = null;

    if (latCol != null && lonCol != null) {
      const latDecoded = decodeLatLong(getCellValue(row, latCol));
      const lonDecoded = decodeLatLong(getCellValue(row, lonCol), false);
      if (!latDecoded[1]) {
        errors.push(latCol);
      } else {
        latitude = latDecoded[0];
      }
      if (!lonDecoded[1]) {
        errors.push(lonCol);
      } else {
        longitude = lonDecoded[0];
      }
    } else if (mgrsCol != null) {
      try {
        const parsed = parseMGRS(getCellValue(row, mgrsCol));
        latitude = parsed.lat;
        longitude = parsed.lon;
      } catch (error) {
        errors.push(mgrsCol);
      }
    } else if (utmCol != null) {
      try {
        const parsed = tryParseUTM(getCellValue(row, utmCol));
        latitude = parsed.lat;
        longitude = parsed.lon;
      } catch (error) {
        errors.push(utmCol);
      }
    }

    let nodeType = '';
    if (areNetworkSites && nodeTypeCol != null) {
      nodeType = getCellValue(row, nodeTypeCol).toUpperCase();
      if (nodeType && !['DN', 'POP'].includes(nodeType)) {
        errors.push(nodeTypeCol);
      }
    }

    let nearbySiteExists = false;
    if (latitude != null && longitude != null) {
      nearbySiteExists = getNearbySites(sites, [longitude, latitude]);
    }

    const parsedRow: ParsedSiteCSVRow = {
      data: row,
      errors,
      nearbySite: nearbySiteExists,
      truncates,
    };

    if (!errors.length && !columnError && latitude != null && longitude != null) {
      parsedRow.siteData = {
        name,
        latitude,
        longitude,
        height_units: 'm',
        description,
        is_network_site: areNetworkSites,
        ...(maximumHeight != null ? { maximum_height: maximumHeight } : {}),
        ...(areNetworkSites ? { node_type: nodeType } : {}),
      };
    }

    if (truncates.length) {
      truncData.push(parsedRow);
    }
    if (errors.length) {
      errorData.push(parsedRow);
    } else if (nearbySiteExists) {
      siteWithNearSites.push(parsedRow);
    } else {
      validData.push(parsedRow);
    }
  });

  const importableRows = [...validData, ...siteWithNearSites];

  return {
    validData,
    errorData,
    siteWithNearSites,
    truncData,
    isEmpty,
    totalRows: data.length,
    columnError,
    importableRows,
  };
}
